import React, { useState } from "react";
import { Checkbox, FormControlLabel, Menu, MenuItem, Typography } from "@mui/material";
import ViewColumnIcon from "@mui/icons-material/ViewColumn";
import Button from "./atoms/Button";
import { BRAND_COLORS } from "../constants/taskStatuses";

/**
 * "Columns" toolbar button with a show/hide checklist for a task grid (COL-150).
 * State and persistence come from useGridColumnPrefs.
 *
 * @param {Object[]} columns          - Full column definitions ({ id, label })
 * @param {string[]} mandatoryColumns - Column ids that are always shown (not listed)
 * @param {Function} isColumnVisible  - (columnId) => boolean
 * @param {Function} onToggle         - (columns, columnId) => void
 * @param {Function} onReset          - (columns) => void
 * @param {string}   testId           - data-testid for the button
 */
const ColumnPickerMenu = ({ columns, mandatoryColumns, isColumnVisible, onToggle, onReset, testId }) => {
    const [anchorEl, setAnchorEl] = useState(null);

    return (
        <>
            <Button
                variant="secondary"
                onClick={(e) => setAnchorEl(e.currentTarget)}
                startIcon={<ViewColumnIcon sx={{ fontSize: 18 }} />}
                sx={{ height: 36, fontWeight: 500, borderRadius: "6px", textTransform: "none" }}
                data-testid={testId}
            >
                Columns
            </Button>
            <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={() => setAnchorEl(null)}
                PaperProps={{ sx: { maxHeight: 420, minWidth: 240 } }}
            >
                <Typography sx={{ px: 2, py: 1, fontSize: "12px", fontWeight: 700, color: "#64748B" }}>
                    Show columns
                </Typography>
                {columns.filter(c => !mandatoryColumns.includes(c.id)).map(col => (
                    <MenuItem key={col.id} dense onClick={() => onToggle(columns, col.id)} sx={{ py: 0 }}>
                        <FormControlLabel
                            control={<Checkbox
                                size="small"
                                checked={isColumnVisible(col.id)}
                                tabIndex={-1}
                                disableRipple
                                // Match the row-select checkboxes (TaskTable.js); without this
                                // MUI falls back to its own primary, not the Trexo blue.
                                sx={{
                                    color: BRAND_COLORS.BORDER_LIGHT,
                                    "&.Mui-checked": { color: `${BRAND_COLORS.PRIMARY} !important` }
                                }}
                            />}
                            label={col.label}
                            sx={{ "& .MuiFormControlLabel-label": { fontSize: "13px" } }}
                            onClick={(e) => e.preventDefault()}
                        />
                    </MenuItem>
                ))}
                <MenuItem dense onClick={() => { onReset(columns); setAnchorEl(null); }} sx={{ mt: 0.5, fontSize: "13px", color: BRAND_COLORS.PRIMARY, fontWeight: 600 }}>
                    Reset to default
                </MenuItem>
            </Menu>
        </>
    );
};

export default ColumnPickerMenu;
